const parseSystemDataValue = (systemData) => {
  if (!systemData) {
    return null;
  }

  if (typeof systemData === "string") {
    try {
      return JSON.parse(systemData);
    } catch (error) {
      console.error("Failed to parse systemData payload", error);
      return null;
    }
  }

  if (typeof systemData === "object") {
    return systemData;
  }

  return null;
};

export const getSystemEnvironments = (systemData) => {
  const parsedSystemData = parseSystemDataValue(systemData);
  if (!parsedSystemData) {
    return [];
  }

  if (
    parsedSystemData.json &&
    typeof parsedSystemData.json === "string" &&
    parsedSystemData.json.trim()
  ) {
    try {
      const embeddedSystemData = JSON.parse(parsedSystemData.json);
      if (
        embeddedSystemData?.environments &&
        typeof embeddedSystemData.environments === "object"
      ) {
        return Object.values(embeddedSystemData.environments);
      }
    } catch (error) {
      console.error("Failed to parse embedded systemData.json payload", error);
    }
  }

  if (
    parsedSystemData.environments &&
    typeof parsedSystemData.environments === "object"
  ) {
    return Object.values(parsedSystemData.environments);
  }

  return [];
};

const getElementIdCandidates = (elementData) => {
  const candidates = [
    elementData?.id,
    elementData?.elementID,
    elementData?.elementId,
    elementData?.elementData?.id,
  ];

  return candidates
    .filter((candidate) => candidate !== null && candidate !== undefined)
    .map((candidate) => String(candidate).trim())
    .filter(Boolean);
};

const getEnvironmentElementIdCandidates = (environmentElement) => {
  const candidates = [
    environmentElement?.elementID,
    environmentElement?.elementId,
    environmentElement?.id,
    environmentElement?.elementData?.id,
  ];

  return candidates
    .filter((candidate) => candidate !== null && candidate !== undefined)
    .map((candidate) => String(candidate).trim())
    .filter(Boolean);
};

const getTargetEnvironmentId = (environmentElement) => {
  const targetEnvironmentId =
    environmentElement?.targetEnvironmentId ||
    environmentElement?.targetEnvironmentID ||
    environmentElement?.targetEnvironmentData?.id ||
    "";

  return String(targetEnvironmentId).trim();
};

const getEnvironmentIdCandidates = (environment) =>
  [environment?.id, environment?.environmentId, environment?.environmentID]
    .filter((candidate) => candidate !== null && candidate !== undefined)
    .map((candidate) => String(candidate).trim())
    .filter(Boolean);

export const getSelectedElementTargetEnvironmentId = ({
  systemData,
  currentEnvironmentId,
  elementData,
}) => {
  const selectedElementIds = getElementIdCandidates(elementData);
  if (!selectedElementIds.length) {
    return "";
  }

  const selectedElementIdSet = new Set(selectedElementIds);
  const environments = getSystemEnvironments(systemData);
  const validEnvironmentIds = new Set(
    environments.flatMap((environment) => getEnvironmentIdCandidates(environment)),
  );
  const normalizedCurrentEnvironmentId = String(
    currentEnvironmentId ?? "",
  ).trim();
  const currentEnvironment = environments.find(
    (environment) => {
      const environmentIds = getEnvironmentIdCandidates(environment);
      return environmentIds.includes(normalizedCurrentEnvironmentId);
    },
  );
  const candidateEnvironments = currentEnvironment
    ? [currentEnvironment]
    : environments;

  for (const environment of candidateEnvironments) {
    const environmentElements = Array.isArray(environment?.elements)
      ? environment.elements
      : [];

    for (const environmentElement of environmentElements) {
      const environmentElementIds =
        getEnvironmentElementIdCandidates(environmentElement);
      const matchesSelectedElement = environmentElementIds.some((id) =>
        selectedElementIdSet.has(id),
      );

      if (matchesSelectedElement) {
        const targetEnvironmentId = getTargetEnvironmentId(environmentElement);
        return validEnvironmentIds.has(targetEnvironmentId)
          ? targetEnvironmentId
          : "";
      }
    }
  }

  return "";
};
